<template>
  <div class="admin-program">
    <admin-view-header title="Program telewizyjny" />
    <div class="tabs">
      <ul>
        <li :class="{ 'is-active': tab === 'program' }">
          <a @click="switchTab('program')">
            <icon name="fa-list" />
            <span>Program</span>
          </a>
        </li>
        <li :class="{ 'is-active': tab === 'blocks' }">
          <a @click="switchTab('blocks')">
            <icon name="fa-th-large" />
            <span>Bloki programowe</span>
          </a>
        </li>
      </ul>
    </div>
    <loader :state="actionsValues" without-loading v-if="tab === 'program'">
      <week-calendar
        :program="program"
        :blocks="blocks"
        :delta="delta"
        :day="day"
        @go-to="goToDay"
        @new-program-item="openNewProgramItemModal"
        @edit-program-item="openEditProgramItemModal"
        @delete-program-item="openProgramItemRemovalConfirmationModal"
      />

      <template #error>
        <div class="grey-message">
          <icon name="fa-times-circle" />
          <p>Dane niedostępne z powodu braku połączenia z serwerem.</p>
        </div>
      </template>
    </loader>
    <loader :state="actions.blocks" without-loading v-else>
      <program-blocks
        :blocks="blocks"
        @new-program-block="openNewProgramBlockModal"
        @edit-program-block="openEditProgramBlockModal"
        @swap-program-blocks="swapProgramBlocks"
        @delete-program-block="openProgramBlockRemovalConfirmationModal"
      />

      <template #error>
        <div class="grey-message">
          <icon name="fa-times-circle" />
          <p>Dane niedostępne z powodu braku połączenia z serwerem.</p>
        </div>
      </template>
    </loader>
  </div>
</template>

<script>
import dayjs from "dayjs";

import { mapState, mapGetters } from "vuex";

import {
  REQUEST_BLOCKS,
  ADD_PROGRAM_BLOCK,
  UPDATE_PROGRAM_BLOCK,
  SWAP_PROGRAM_BLOCKS,
  DELETE_PROGRAM_BLOCK,
  REQUEST_PROGRAM,
  ADD_PROGRAM_ITEM,
  UPDATE_PROGRAM_ITEM,
  DELETE_PROGRAM_ITEM
} from ":actions";

import AdminViewHeader from "%admin/components/global/AdminViewHeader";
import WeekCalendar from "%admin/components/global/WeekCalendar";
import ProgramBlocks from "%admin/components/program/ProgramBlocks";
import { ProgramItemModal, ProgramBlockModal, YesNoCancelModal } from "%modals";
import { SuccessNotification } from "%notifications";
import { Icon } from "%helpers";
import { WithDispatcherProgress } from "%mixins";

export default {
  name: "admin-program",

  components: {
    AdminViewHeader,
    WeekCalendar,
    ProgramBlocks,
    Icon
  },

  mixins: [WithDispatcherProgress],

  data() {
    return {
      delta: 3,
      day: dayjs(),
      tab: "program"
    };
  },

  computed: {
    ...mapState({
      program: state => state.program.program,
      blocks: state => state.program.blocks
    }),

    ...mapGetters(["programBlocks"])
  },

  beforeMount() {
    this.dispatchWithProgress("blocks", REQUEST_BLOCKS);
    this.dispatchWithProgress("program", REQUEST_PROGRAM, {
      from: this.day.format("YYYY-MM-DD"),
      to: this.day.add(this.delta, "day").format("YYYY-MM-DD")
    });
  },

  methods: {
    switchTab(tab) {
      this.tab = tab;
    },

    goToDay(day) {
      this.day = day;
    },

    openNewProgramItemModal({ date, block }) {
      this.$emit("show-modal", {
        component: ProgramItemModal,
        props: {
          date,
          block,
          onSubmit: item => this.addProgramItem(item)
        }
      });
    },

    openEditProgramItemModal({ date, block, item }) {
      this.$emit("show-modal", {
        component: ProgramItemModal,
        props: {
          date,
          block,
          item,
          onSubmit: (item, id) => this.updateProgramItem(id, item)
        }
      });
    },

    addProgramItem(item) {
      return this.$store
        .dispatch(ADD_PROGRAM_ITEM, { item })
        .then(() => this.successNotification("Program został utworzony."));
    },

    updateProgramItem(id, item) {
      return this.$store
        .dispatch(UPDATE_PROGRAM_ITEM, {
          id,
          item
        })
        .then(() => this.successNotification("Program został zaktualizowany."));
    },

    successNotification(content) {
      this.$emit("push-notification", {
        component: SuccessNotification,
        props: {
          content
        }
      });
    },

    openProgramItemRemovalConfirmationModal(item) {
      this.$emit("show-modal", {
        component: YesNoCancelModal,
        props: {
          title: "Potwierdzenie usunięcia",
          content: `<p>Czy na pewno chcesz usunąć punkt programu o poniższym tytule?</p>
                    <blockquote>${item.title}</blockquote>`,

          proceed: {
            type: "is-danger",
            icon: "fa-trash",
            content: "Usuń",
            callback: () => this.deleteProgramItem(item.id)
          },

          cancel: {
            content: "Anuluj"
          }
        }
      });
    },

    deleteProgramItem(id) {
      this.$store.dispatch(DELETE_PROGRAM_ITEM, { id });
    },

    openNewProgramBlockModal() {
      this.$emit("show-modal", {
        component: ProgramBlockModal,
        props: {
          onSubmit: item => this.addProgramBlock(item)
        }
      });
    },

    openEditProgramBlockModal(block) {
      this.$emit("show-modal", {
        component: ProgramBlockModal,
        props: {
          block,
          onSubmit: b => this.updateProgramBlock(block.id, b)
        }
      });
    },

    openProgramBlockRemovalConfirmationModal(block) {
      this.$emit("show-modal", {
        component: YesNoCancelModal,
        props: {
          title: "Potwierdzenie usunięcia",
          content: `<p>Czy na pewno chcesz usunąć blok programowy o poniższej nazwie?</p>                    
                    <blockquote>${block.name}</blockquote>
                    <strong>Uwaga!</strong> Usunięcie bloku programowego skutkuje
                    <strong>nieodwracalnym</strong> usunięciem wszystkich programów
                    do niego przypisanych!
                    `,

          proceed: {
            type: "is-danger",
            icon: "fa-trash",
            content: "Usuń",
            callback: () => this.deleteProgramBlock(block.id)
          },

          cancel: {
            content: "Anuluj"
          }
        }
      });
    },

    addProgramBlock(block) {
      return this.$store
        .dispatch(ADD_PROGRAM_BLOCK, { block })
        .then(() =>
          this.successNotification("Blok programowy został utworzony.")
        );
    },

    updateProgramBlock(id, block) {
      return this.$store
        .dispatch(UPDATE_PROGRAM_BLOCK, { id, block })
        .then(() =>
          this.successNotification("Blok programowy został zaktualizowany.")
        );
    },

    swapProgramBlocks(swap) {
      this.$store.dispatch(SWAP_PROGRAM_BLOCKS, { swap });
    },

    deleteProgramBlock(id) {
      this.$store.dispatch(DELETE_PROGRAM_BLOCK, { id });
    }
  },

  watch: {
    day() {
      this.dispatchWithProgress("blocks", REQUEST_BLOCKS);
      this.dispatchWithProgress("program", REQUEST_PROGRAM, {
        from: this.day.format("YYYY-MM-DD"),
        to: this.day.add(this.delta, "day").format("YYYY-MM-DD")
      });
    }
  }
};
</script>

<style></style>
